import { seedFieldTypeDefaults } from '@resource-kit/controllers';
import { FIELD_TYPE_REGISTRY, FIELD_TYPE_WIDGETS } from '@resource-kit/ui/react';
import { registerAdminWidgets } from './admin/widgets';
import { registerAuditWidgets } from './audit/widgets';
import { registerCustomerWidgets } from './customers/widgets';
import { registerDriverProfileWidgets } from './driver-profiles/widgets';
import { registerDriverWidgets } from './drivers/widgets';
import { registerInventoryWidgets } from './inventory/widgets';
import { registerJobWidgets } from './dispatch/widgets';
import { registerOrderWidgets } from './orders/widgets';
import { registerPurchaseOrderWidgets } from './procurement/widgets';
import { registerProductWidgets } from './products/widgets';
import { registerStocktakeWidgets } from './stocktake/widgets';
import { registerUserWidgets } from './users/widgets';
import { registerWebhookWidgets } from './webhooks/widgets';

/**
 * The one explicit widget-registration call (design §5.2 discipline 2).
 *
 * Every feature that owns widgets exports a `registerXxxWidgets()` and is
 * listed here; each composition root calls this barrel once, before the tree
 * mounts. Deliberately NOT a bare `import './widgets'` side
 * effect: import order would decide registration timing, and a bundler is free
 * to tree-shake an import whose only purpose is a side effect.
 *
 * Calling it twice is an authoring mistake, not an idempotent no-op — the
 * duplicate is reported by `registerWidget`'s failure policy.
 */
export function registerFeatureWidgets(): void {
  // Level 3 of the widget resolution order, and the field types' default
  // validation schemas. The catalog lives in `@resource-kit/controllers` since
  // #2167 PR-3 and starts EMPTY: a package cannot import
  // `@lib/field-type-registry`, so the host installs it here (ADR-0035 D4).
  // This is the only PRODUCTION `seedFieldTypeDefaults` call site; the only
  // other calls to it are in
  // `src/__tests__/resource-kit/resource-kit-ui/widget-seeding.test.ts`,
  // which seed throwaway `vi.resetModules()` instances of the catalog, never
  // this one — that suite never calls this function.
  // Standing ahead of the `registerXxxWidgets()` calls is incidental, though:
  // registration only `Map.set`s a name — `registerWidget`,
  // `packages/resource-kit-controllers/widgets/registry.ts::registerWidget` — and resolves
  // nothing. The constraint that IS load-bearing is seed-before-RESOLVE —
  // enforced by `resolveFieldWidget`
  // (`packages/resource-kit-controllers/widgets/registry.ts::resolveFieldWidget`) and
  // `resolveTypeZodBase`
  // (`packages/resource-kit-controllers/widgets/registry.ts::resolveTypeZodBase`) failing hard
  // on an unseeded catalog, and satisfied by all three COMPOSITION-ROOT callers
  // of this function running before anything renders (`src/main.tsx`,
  // `src/test-setup.ts`, and `ds-providers.tsx` for the design-sync
  // preview harness, which mounts kit components outside the app build).
  // Suites call it too — `git grep -n 'registerFeatureWidgets()' -- frontend`
  // lists every call site — in two shapes.
  // `src/__tests__/resource-kit/components-resource/ResourcePage.getRowId.test.tsx`
  // and
  // `src/__tests__/resource-kit/resource-kit-ui-form/FormView.pageShell.test.tsx`
  // run
  // `vi.resetModules()` from a `vi.hoisted` block and then seed the FRESH
  // module instance that leaves them holding — NOT the one the three roots
  // seeded. `src/features/__tests__/widgets.test.ts` and
  // `src/lib/__tests__/filter-type.test.ts` instead call
  // `resetWidgetRegistry()` and re-call this function on the shared instance.
  // Every other suite just relies on `src/test-setup.ts` having seeded it.
  seedFieldTypeDefaults({
    widgets: FIELD_TYPE_WIDGETS,
    zodBaseFor: (field) => FIELD_TYPE_REGISTRY[field.type].zodBase(field),
  });
  registerOrderWidgets();
  registerAdminWidgets();
  registerAuditWidgets();
  registerCustomerWidgets();
  registerDriverProfileWidgets();
  registerDriverWidgets();
  registerInventoryWidgets();
  registerJobWidgets();
  registerProductWidgets();
  registerPurchaseOrderWidgets();
  registerStocktakeWidgets();
  registerUserWidgets();
  registerWebhookWidgets();
}
